import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../../api/client";
import StatusBadge from "../shared/StatusBadge";
import "./AdminDashboard.css";

const formatCAD = (n) =>
    new Intl.NumberFormat("en-CA", {
        style: "currency",
        currency: "CAD",
    }).format(n ?? 0);

const StatCard = ({ label, value, accent }) => (
    <div className="stat-card">
        <span className="stat-card__label">{label}</span>
        <span
            className={`stat-card__value ${accent ? "stat-card__value--accent" : ""}`}
        >
            {value}
        </span>
    </div>
);

const AdminDashboard = () => {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        api.adminStats()
            .then((d) => setData(d))
            .catch(() => setError(t("common.error")))
            .finally(() => setLoading(false));
    }, [t]);

    if (loading)
        return <p className="admin-dash__loading">{t("common.loading")}</p>;
    if (error) return <div className="error-message">{error}</div>;

    const { stats, recent_quotes, recent_invoices } = data;

    return (
        <div className="admin-dash">
            <div className="section-header">
                <h2>{t("dashboard.welcome")}</h2>
            </div>

            {stats.pending_clients > 0 && (
                <div className="admin-dash__alert">
                    <strong>{stats.pending_clients}</strong> client
                    {stats.pending_clients !== 1 ? "s" : ""} awaiting approval.{" "}
                    <Link to="/admin/clients">{t("clients.pending")} →</Link>
                </div>
            )}

            <div className="admin-dash__stats">
                <StatCard
                    label={t("dashboard.openQuotes")}
                    value={stats.open_quotes}
                />
                <StatCard
                    label={t("dashboard.unpaidInvoices")}
                    value={stats.unpaid_invoices}
                />
                <StatCard
                    label={t("dashboard.activeClients")}
                    value={stats.active_clients}
                />
                <StatCard
                    label={t("dashboard.pendingApprovals")}
                    value={stats.pending_clients}
                />
                <StatCard
                    label={t("dashboard.totalRevenue")}
                    value={formatCAD(stats.revenue_total)}
                    accent
                />
            </div>

            <div className="admin-dash__tables">
                <div className="admin-dash__table-section">
                    <div className="admin-dash__table-header">
                        <h3>{t("dashboard.recentQuotes")}</h3>
                        <Link
                            to="/admin/quotes/new"
                            className="btn btn--primary btn--sm"
                        >
                            {t("quote.new")}
                        </Link>
                    </div>
                    <table className="dash-table">
                        <thead>
                            <tr>
                                <th>{t("quote.quoteTitle")}</th>
                                <th>{t("quote.client")}</th>
                                <th>{t("quote.status")}</th>
                                <th className="dash-table__right">
                                    {t("quote.total")}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {recent_quotes.length === 0 ? (
                                <tr>
                                    <td
                                        colSpan={4}
                                        className="dash-table__empty"
                                    >
                                        {t("quote.noQuotes")}
                                    </td>
                                </tr>
                            ) : (
                                recent_quotes.map((q) => (
                                    <tr key={q.id}>
                                        <td>
                                            <Link to={`/admin/quotes/${q.id}`}>
                                                {q.title}
                                            </Link>
                                        </td>
                                        <td className="dash-table__muted">
                                            {q.client?.name}
                                        </td>
                                        <td>
                                            <StatusBadge status={q.status} />
                                        </td>
                                        <td className="dash-table__right dash-table__mono">
                                            {formatCAD(q.total)}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="admin-dash__table-section">
                    <div className="admin-dash__table-header">
                        <h3>{t("dashboard.recentInvoices")}</h3>
                        <Link
                            to="/admin/invoices/new"
                            className="btn btn--primary btn--sm"
                        >
                            {t("invoice.new")}
                        </Link>
                    </div>
                    <table className="dash-table">
                        <thead>
                            <tr>
                                <th>{t("invoice.number")}</th>
                                <th>{t("invoice.client")}</th>
                                <th>{t("invoice.status")}</th>
                                <th className="dash-table__right">
                                    {t("invoice.total")}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {recent_invoices.length === 0 ? (
                                <tr>
                                    <td
                                        colSpan={4}
                                        className="dash-table__empty"
                                    >
                                        {t("invoice.noInvoices")}
                                    </td>
                                </tr>
                            ) : (
                                recent_invoices.map((inv) => (
                                    <tr key={inv.id}>
                                        <td>
                                            <Link
                                                to={`/admin/invoices/${inv.id}`}
                                                className="dash-table__mono"
                                            >
                                                {inv.invoice_number}
                                            </Link>
                                        </td>
                                        <td className="dash-table__muted">
                                            {inv.client?.name}
                                        </td>
                                        <td>
                                            <StatusBadge status={inv.status} />
                                        </td>
                                        <td className="dash-table__right dash-table__mono">
                                            {formatCAD(inv.total)}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default AdminDashboard;
